
import React, { useState, useEffect, useCallback } from "react";
import { useDeviceDetection } from "@/hooks/useDeviceDetection";
import { useCortexChat } from "@/contexts/CortexChatContext";
import { useCurrentOrganizationId } from "@/hooks/useCurrentOrganization";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { cn } from '@flowstate/utils';
import { ChatSidebarProps } from "../types/chat-sidebar-types";
import { useSidebarState } from "../hooks/useSidebarState";
import { useChatGestures } from "../hooks/useChatGestures";
import { useContextWindowUsage } from "../hooks/useContextWindowUsage";
import { submitCortexBackgroundJob } from "@flowstate/quick-actions";
import { supabase } from "@/integrations/supabase/client";
import { getNotificationsCollection } from "@/lib/db";
import { setAccessToken } from "@/lib/db/auth-store";
import { ChatMainColumn } from "./chat-sidebar/ChatMainColumn";
import { logger } from "@/lib/logger";

const ChatSidebar = ({
  isOpen,
  onClose,
  onToggleFullscreen,
  isFullscreen,
  isInitializing: isInitializingProp,
}: ChatSidebarProps) => {
  const {
    activeSidebar,
    setActiveSidebar,
    toggleSidebar: _toggleSidebar,
  } = useSidebarState({
    initialIsOpen: isOpen,
    onClose,
    onToggleFullscreen,
    externalIsFullscreen: isFullscreen,
  });

  const { isMobile } = useDeviceDetection();
  const currentOrgId = useCurrentOrganizationId();
  const { currentProject } = useCurrentProject();

  const {
    messages,
    input,
    setInput,
    isLoading,
    isLoadingConversation,
    isError,
    handleSendMessage,
    conversationList,
    currentConversationId,
    startNewConversation,
    loadConversation,
    deleteConversation,
    toggleContextSelection,
    summarizeSelectedConversations,
    handleFileUploaded,
    pendingAttachments,
    selectedContextIds,
    updateMessageTodoList: _updateMessageTodoList,
    isSummarizing,
    summarizedContext,
    handleReplayMessage,
    autoExtractTasks,
    setAutoExtractTasks,
    selectedSpecialist,
    setSelectedSpecialist,
    selectedNotebookId,
    setSelectedNotebookId,
    selectedModel,
    setSelectedModel,
    selectedProvider,
    setSelectedProvider,
    reasoningEffort,
    setReasoningEffort,
    isGeminiOAuthConnected,
    pendingQuestion,
    answerPendingQuestion,
  } = useCortexChat();

  const isInitializing = isInitializingProp || isLoadingConversation;

  const [backgroundMode, setBackgroundMode] = useState(() => {
    try {
      return localStorage.getItem("cortex-background-mode") === "true";
    } catch {
      return false;
    }
  });

  const handleToggleBackgroundMode = useCallback(() => {
    setBackgroundMode((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("cortex-background-mode", String(next));
      } catch {
        /* localStorage unavailable */
      }
      return next;
    });
  }, []);

  const handleSendWithBackgroundMode = useCallback(
    async (text?: string) => {
      if (!backgroundMode) {
        return handleSendMessage(text);
      }

      const prompt = (text ?? input).trim();
      if (!prompt) return;

      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const session = sessionData?.session;
        if (!session?.access_token || !session?.user?.id) {
          const { toast } = await import("@/components/ui/sonner");
          toast.error("Sign in required to submit background jobs");
          return;
        }

        const userId = session.user.id;

        const queueResult = await submitCortexBackgroundJob({
          supabase,
          userId,
          orgId: currentOrgId ?? null,
          prompt,
          specialist: selectedSpecialist ?? undefined,
        });

        if (queueResult.error) {
          const { toast } = await import("@/components/ui/sonner");
          toast.error("Failed to queue background job", {
            description: queueResult.error,
          });
          return;
        }

        const { taskId, dedupKey } = queueResult;

        try {
          // Collection insert → write-through persists; Notification Center
          // sees it optimistically via the shared notifications shape.
          setAccessToken(session.access_token);
          await getNotificationsCollection(userId).insert({
            id: crypto.randomUUID(),
            user_id: userId,
            actor_id: null,
            type: "CORTEX_JOB_QUEUED",
            entity_type: "cortex_task",
            entity_id: taskId ?? null,
            message_preview: `⚡ "${prompt.slice(0, 60)}${prompt.length > 60 ? "..." : ""}" queued`,
            data: {
              prompt: prompt.slice(0, 200),
              specialist: selectedSpecialist ?? null,
              dedup_key: dedupKey,
            },
            is_read: false,
            read_at: null,
            created_at: new Date().toISOString(),
          });
        } catch {
          /* non-fatal */
        }

        const { toast } = await import("@/components/ui/sonner");
        toast.success("⚡ Job queued", {
          description:
            "Cortex is working on it — you'll be notified when complete.",
        });
        setInput("");
      } catch (err) {
        const { toast } = await import("@/components/ui/sonner");
        toast.error("Failed to submit background job", {
          description: err instanceof Error ? err.message : "Unknown error",
        });
      }
    },
    [
      backgroundMode,
      handleSendMessage,
      input,
      setInput,
      currentOrgId,
      selectedSpecialist,
    ],
  );

  const [_swipeIndicator, setSwipeIndicator] = useState<"left" | "right" | null>(
    null,
  );
  const { swipeHandlers } = useChatGestures({
    onClose,
    isOpen,
    isMobile,
    setSwipeIndicator,
  });

  const contextUsage = useContextWindowUsage(
    messages,
    selectedModel,
    selectedProvider,
  );

  const getActionItemsKey = useCallback(
    (orgId?: string | null) => `chat-action-items-${orgId || "global"}`,
    [],
  );

  const [allActionItems, setAllActionItems] = useState<any[]>([]);

  useEffect(() => {
    try {
      const key = getActionItemsKey(currentOrgId);
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        setAllActionItems(Array.isArray(parsed) ? parsed : []);
      } else {
        setAllActionItems([]);
      }
    } catch (e) {
      logger.error("Failed to load action items", { error: e });
    }
  }, [currentOrgId, getActionItemsKey]);

  const handleActionItemsUpdate = useCallback(
    (newItems: any[]) => {
      setAllActionItems(newItems);
      try {
        const key = getActionItemsKey(currentOrgId);
        localStorage.setItem(key, JSON.stringify(newItems));
      } catch (e) {
        logger.error("Failed to save action items", { error: e });
      }
    },
    [currentOrgId, getActionItemsKey],
  );

  const executeQuickAction = useCallback(
    (action: string) => {
      handleSendMessage(action);
      setActiveSidebar(null);
    },
    [handleSendMessage, setActiveSidebar],
  );

  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const handleConversationClick = (
    id: string,
    _title: string,
    _event: React.MouseEvent,
  ) => {
    loadConversation(id);
    setActiveSidebar(null);
  };

  const columnProps = {
    isFullscreen,
    onClose,
    onToggleFullscreen,
    activeSidebar,
    setActiveSidebar,
    currentConversation:
      conversationList.find((c) => c.id === currentConversationId) || null,
    startNewConversation,
    isMobile,
    messages,
    isError,
    handleReplayMessage,
    onFollowUpClick: handleSendMessage,
    isInitializing,
    input,
    setInput,
    handleSendMessage: async (text?: string) => {
      await handleSendWithBackgroundMode(text);
    },
    isLoading,
    currentConversationId: currentConversationId ?? undefined,
    handleFileUploaded,
    pendingAttachments,
    autoExtractTasks,
    onToggleAutoExtract: () => setAutoExtractTasks((prev) => !prev),
    selectedSpecialist,
    onSpecialistChange: setSelectedSpecialist,
    selectedProvider,
    onProviderChange: setSelectedProvider,
    selectedModel,
    onModelChange: setSelectedModel,
    reasoningEffort,
    onReasoningEffortChange: setReasoningEffort,
    isGeminiOAuthConnected,
    backgroundMode,
    onToggleBackgroundMode: handleToggleBackgroundMode,
    selectedNotebookId: selectedNotebookId ?? undefined,
    onSelectNotebook: (id: string | undefined) => setSelectedNotebookId(id ?? undefined),
    contextUsage,
    pendingQuestion,
    onAnswerQuestion: (opts: { approved?: boolean; value?: string }) => void answerPendingQuestion(opts),
    allActionItems,
    handleActionItemsUpdate,
    executeQuickAction,
    conversationList,
    loadConversation,
    deleteConversation,
    selectedContextIds,
    toggleContextSelection,
    summarizeSelectedConversations,
    isSummarizing,
    summarizedContext: summarizedContext ?? null,
    selectedConversationId,
    setSelectedConversationId,
    tooltipPosition,
    setTooltipPosition,
    handleConversationClick,
  };

  return (
    <div
      ref={swipeHandlers.ref}
      className={cn(
        "flex flex-col h-full min-h-0 w-full overflow-hidden rounded-2xl border border-border/20",
        "bg-background/70 backdrop-blur-xl backdrop-saturate-150 shadow-2xl",
        !isOpen && "pointer-events-none",
      )}
    >
      <ChatMainColumn {...columnProps} />
    </div>
  );
};

export default ChatSidebar;
